import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CustomActionMenu from "@components/CustomActionMenu/CustomActionMenu";
import CustomHeader from "@components/CustomHeader/CustomHeader";
import CustomTable from "@components/CustomTable/CustomTable";
import Loader from "@components/Loader/Loader";
import { CreateModalContext } from "layouts/appLayout/layout";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";

import {
  useGetLeaveRequestsByValidateurQuery,
  useValidateLeaveRequestMutation,
} from "store/conge/congeApi";
import { BASE_URL } from "config/config";
import LeaveRequestModal from "../LeaveRequestModal/LeaveRequestModal";

const LeaveRequestsListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validateLeaveRequest, { isLoading: isValidating }] =
    useValidateLeaveRequestMutation();

  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.supplierId;

  const [openMenuId, setOpenMenuId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  const { data: leaveRequests, isLoading: isLoadingLeaves } =
    useGetLeaveRequestsByValidateurQuery(currentUser._id);

  const { toggleCreateModal } = useContext(CreateModalContext);

  const headers = [
    { key: "type", label: t("congeList.leaves.form.type") },
    { key: "comment", label: t("congeList.leaves.form.comment") },
    { key: "justification", label: t("congeList.leaves.form.justification") },
    { key: "calendarNotes", label: t("congeList.leaves.form.calendar") },
    { key: "isValide", label: t("congeList.leaves.form.status") },
    { key: "createdAt", label: t("congeList.leaves.form.createdAt") },
  ];

  function getImageURL(docId) {
    return `${BASE_URL}leave-requests/${docId}/image`;
  }

  const renderCell = (key, leave) => {
    if (key === "type") return leave?.leaves?.[0]?.type || "-";
    if (key === "comment") return leave?.comment || "-";
    if (key === "justification") {
      return leave?.justification ? (
        <a href={getImageURL(leave._id)} target="_blank" rel="noreferrer">
          {t("congeList.leaves.form.download_justif")}
        </a>
      ) : (
        "-"
      );
    }
    if (key === "calendarNotes") {
      const notes = leave?.leaves?.[0]?.calendarNotes || {};
      return Object.entries(notes)
        .map(([date, value]) => `${date}: ${value}`)
        .join(", ");
    }
    if (key === "isValide")
      return leave?.isValide
        ? t("congeList.common.valid")
        : t("congeList.common.pending");
    if (key === "createdAt")
      return new Date(leave.createdAt).toLocaleDateString();
    return leave[key];
  };

  const handleConsult = (leave) => {
    setOpenMenuId(null);

    toggleCreateModal(
      <LeaveRequestModal
        onClose={() => toggleCreateModal(<></>)}
        leaveRequest={leave}
      />,
      t("congeList.leaves.form.title_edit")
    );
  };

  const actionMenu = (leave) => {
    const actions = [
      {
        label: t("congeList.leaves.actions.view"),
        onClick: () => handleConsult(leave),
      },
      !leave.isValide && {
        label: t("congeList.leaves.actions.validate"),
        onClick: async () => {
          try {
            await validateLeaveRequest(leave._id).unwrap();
            alert(t("congeList.leaves.messages.validated"));
          } catch (err) {
            console.error(err);
            alert(t("congeList.leaves.messages.validate_error"));
          }
        },
      },
    ].filter(Boolean);

    return (
      <CustomActionMenu
        openMenuId={openMenuId}
        setOpenMenuId={setOpenMenuId}
        data={leave}
        actions={actions}
      />
    );
  };

  const onSearchLeave = (text) => {
    setSearchText(text.toLowerCase());
    setCurrentPage(0); // reset pagination après recherche
  };

  const filteredLeaves = useMemo(() => {
    if (!leaveRequests?.data) return [];
    if (searchText.length <= 1) return leaveRequests.data;

    return leaveRequests.data.filter((l) => {
      return (
        l.comment?.toLowerCase().includes(searchText) ||
        l.leaves?.[0]?.type?.toLowerCase().includes(searchText) ||
        l.leaves?.[0]?.description?.toLowerCase().includes(searchText)
      );
    });
  }, [leaveRequests?.data, searchText]);

  // ✅ Pagination
  const pageCount = Math.ceil(filteredLeaves.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentItems = filteredLeaves.slice(offset, offset + itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  if (isLoadingLeaves) return <Loader />;

  return (
    <div className="p-5" style={{ overflowX: "auto" }}>
      <CustomHeader
        title={t("congeList.leaves.header_title")}
        dataLength={filteredLeaves.length}
        onSearch={onSearchLeave}
      />
      <CustomTable
        headers={headers}
        data={currentItems}
        rowKey="_id"
        renderCell={renderCell}
        actionMenu={actionMenu}
      />

      {/* ✅ Pagination en bas */}

      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <ReactPaginate
          previousLabel={"←"}
          nextLabel={"→"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={1}
          pageRangeDisplayed={2}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          activeClassName={"active"}
          forcePage={currentPage}
        />
      </div>
    </div>
  );
};

export default LeaveRequestsListPage;
